import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/toaster'
import { ArrowLeft, Loader2, Mail, CheckCircle } from 'lucide-react'

export function VerifyEmailPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { confirmEmail, resendVerificationCode, pendingVerificationEmail, isLoading } = useAuthStore()

  // Get email from URL params or store
  const emailFromParams = searchParams.get('email')
  const email = emailFromParams || pendingVerificationEmail

  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [resendCooldown, setResendCooldown] = useState(0)
  const [isVerified, setIsVerified] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Redirect if no email to verify
  useEffect(() => {
    if (!email) {
      navigate('/register')
    }
  }, [email, navigate])

  // Countdown timer for resend
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d+$/.test(value)) return

    const newCode = [...code]

    if (value.length > 1) {
      // Handle paste
      const digits = value.slice(0, 6).split('')
      digits.forEach((digit, i) => {
        if (index + i < 6) {
          newCode[index + i] = digit
        }
      })
      setCode(newCode)

      // Focus last filled input or next empty
      const lastIndex = Math.min(index + digits.length, 5)
      inputRefs.current[lastIndex]?.focus()
    } else {
      newCode[index] = value
      setCode(newCode)

      // Auto-focus next input
      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus()
      }
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const verificationCode = code.join('')

    if (verificationCode.length !== 6) {
      toast({
        title: 'Invalid code',
        description: 'Please enter the 6-digit verification code.',
        variant: 'destructive',
      })
      return
    }

    if (!email) return

    try {
      await confirmEmail(email, verificationCode)
      setIsVerified(true)
      toast({
        title: 'Email verified',
        description: 'Your email has been verified. Please login.',
      })
      // Redirect to login after short delay
      setTimeout(() => navigate('/login'), 1500)
    } catch {
      toast({
        title: 'Verification failed',
        description: 'Invalid or expired code. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleResend = async () => {
    if (!email || resendCooldown > 0) return

    try {
      await resendVerificationCode(email)
      setResendCooldown(60)
      toast({
        title: 'Code sent',
        description: 'A new verification code has been sent to your email.',
      })
    } catch {
      toast({
        title: 'Failed to resend',
        description: 'Please try again later.',
        variant: 'destructive',
      })
    }
  }

  if (isVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Email Verified!</h2>
          <p className="text-gray-600">
            Your email has been verified. Redirecting to login...
          </p>
        </div>
      </div>
    )
  }

  if (!email) {
    return null
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex items-center justify-center space-x-2 mb-2">
            <div className="w-10 h-10 bg-gradient-to-r from-spectrum-teal to-spectrum-blue rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">L</span>
            </div>
          </div>
          <h1 className="text-center text-3xl font-bold text-gray-900">LinkToCreative</h1>

          <div className="mt-6 mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
            <Mail className="w-8 h-8 text-blue-600" />
          </div>

          <h2 className="mt-4 text-center text-2xl font-semibold text-gray-900">
            Verify your email
          </h2>
          <p className="mt-2 text-center text-gray-600">
            We've sent a 6-digit verification code to
          </p>
          <p className="text-center font-medium text-gray-900">{email}</p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium text-gray-700 text-center mb-4">
              Enter verification code
            </label>
            <div className="flex justify-center gap-2">
              {code.map((digit, index) => (
                <Input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-12 h-12 text-center text-xl font-semibold"
                  autoFocus={index === 0}
                />
              ))}
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isLoading || code.join('').length !== 6}
          >
            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Verify Email
          </Button>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              Didn't receive the code?{' '}
              <button
                type="button"
                onClick={handleResend}
                disabled={resendCooldown > 0 || isLoading}
                className="text-primary hover:underline disabled:text-gray-400 disabled:no-underline"
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
              </button>
            </p>
          </div>

          <div className="text-center">
            <Link
              to="/register"
              className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to register
            </Link>
          </div>
        </form>
      </div>
    </div>
  )
}
